import {
    Box,
    Dialog,
    DialogContent,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLostCargoReturnPage } from '../context/LostCargoReturnPageContext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { SCUIButton } from '../../../atomic/atoms';
import '../components/LostCargo.scss';
import { ReturnCommand } from '../../../services/command';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { LCSR, lcsrCls } from '../lostCargoSearchReturnPage.classes';

function LostCargoCustomGrid() {
    const { t } = useTranslation();
    const { returnListData, selectedProducts, handleQuantityChange, selectAllProducts, resetSelectedProducts } =
        useLostCargoReturnPage();
    const [openModal, setOpenModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');

    const allProducts =
        returnListData?.shippingListForBackofficeModels?.[0]?.shippingListItemForBackofficeModels?.map(
            (product: any) => ({ barcode: product.barcode, quantity: product.quantity })
        ) || [];

    const handleOpenModal = (imageUrl: any) => {
        setSelectedImage(imageUrl);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Yeni ürün listesi yüklendiğinde seçimleri sıfırla ve tümünü seç
    useEffect(() => {
        if (allProducts.length > 0) {
            resetSelectedProducts();
            selectAllProducts(allProducts);
        }
    }, [returnListData?.shippingListForBackofficeModels?.[0]?.shippingListId]);

    const getTotalSelectedQuantity = () => {
        let total = 0;
        selectedProducts.forEach((quantity) => {
            total += quantity;
        });
        return total;
    };

    const getTotalProductCount = () => {
        return allProducts.reduce((sum: number, product: any) => sum + product.quantity, 0);
    };

    const getRowBackgroundColor = (product: any) => {
        const selectedQuantity = selectedProducts.get(product.barcode) || 0;
        const maxQuantity = product.quantity;

        if (selectedQuantity === 0) {
            return ''; // Seçilmemişse arka plan yok
        } else if (selectedQuantity === maxQuantity) {
            return '#75c693'; // Tam seçiliyse yeşil
        } else {
            return '#FFF9C4'; // Eksik seçiliyse pastel sarı
        }
    };

    const handleCreateRequest = () => {
        // Sadece quantity > 0 olan ürünleri filtrele
        const selectedProductsArray = Array.from(selectedProducts.entries())
            .filter(([_, quantity]) => quantity > 0)
            .map(([barcode, quantity]) => ({
                barcode,
                quantity,
            }));

        if (selectedProductsArray.length === 0) {
            Swal.fire({
                title: t('general.error'),
                text: t('lostCargoReturnPageBuilder.noProductSelected'),
                icon: 'warning',
                confirmButtonText: t('lostCargoReturnPageBuilder.ok'),
            });
            return;
        }

        let req = {
            customerId: returnListData.customerId,
            orderId: returnListData.orderId,
            isMarketPlaceReturn: false,
            shippingListId: returnListData?.shippingListForBackofficeModels[0]?.shippingListId,
            shippingListNo: returnListData?.shippingListForBackofficeModels[0]?.shippingListNo,
            selectedProducts: selectedProductsArray,
        };
        ReturnCommand.CreateReturnForLostCargo(req).then((res: any) => {
            if (res?.returnApprovedMessageModel?.createReturnSuccessMessage) {
                let htmlContent = `${res?.returnApprovedMessageModel?.createReturnSuccessMessage}
            <br><br>
            ${res?.returnApprovedMessageModel?.returnCode}`;
                Swal.fire({
                    title: t('lostCargoReturnPageBuilder.returnSuccess'),
                    html: htmlContent,
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true,
                }).then(() => {
                    window.location.href = '/returnList?RMACode=' + res?.rmaCode + '&take=10';
                });
            } else {
                Swal.fire('Error').then(() => {
                    window.location.href = '/lost-cargo-return-page';
                });
            }
        });
    };
    return (
        <Box className={LCSR.gridOuter} sx={{ marginLeft: '1.5rem' }}>
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                slotProps={{
                    paper: {
                        className: LCSR.imagePreviewDialogPaper,
                        style: {
                            position: 'relative',
                            padding: '20px',
                        },
                    },
                }}
            >
                <IconButton
                    className={LCSR.inputIconImagePreviewClose}
                    onClick={handleCloseModal}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'gray',
                        '&:hover': {
                            color: 'black',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: '24px' }} />
                </IconButton>
                <DialogContent className={LCSR.imagePreviewContent} sx={{ padding: '24px 16px 16px 16px' }}>
                    <img className={LCSR.imagePreviewImg} src={selectedImage} style={{ width: '400px', height: 'auto' }} alt="" />
                </DialogContent>
            </Dialog>
            <Grid container spacing={2} className={lcsrCls('infoContainer', LCSR.gridPaper)}>
                <Grid
                    size={{
                        xs: 12,
                    }}
                >
                    {returnListData?.shippingListForBackofficeModels[0]?.shippingListItemForBackofficeModels && (
                            <TableContainer className={LCSR.productsTableContainer} component={Paper} variant="outlined">
                                <Table className={LCSR.productsTable} size="small" aria-label="a dense table">
                                    <TableHead>
                                        <TableRow sx={{ height: 50 }}>
                                            <TableCell style={{ textAlign: 'center' }}>
                                                <strong>{t('lostCargoReturnPageBuilder.productPhoto')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }}>
                                                <strong>{t('lostCargoReturnPageBuilder.barcode')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                                <strong>{t('lostCargoReturnPageBuilder.specialAndColorCode')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                                <strong>{t('lostCargoReturnPageBuilder.productName')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                                <strong>{t('lostCargoReturnPageBuilder.size')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                                <strong>{t('lostCargoReturnPageBuilder.returnReason')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                                <strong>{t('lostCargoReturnPageBuilder.quantity')}</strong>
                                            </TableCell>
                                            <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                                <strong>{t('lostCargoReturnPageBuilder.price')}</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {returnListData?.shippingListForBackofficeModels &&
                                            returnListData?.shippingListForBackofficeModels[0]?.shippingListItemForBackofficeModels.map(
                                                (product: any) => (
                                                    <>
                                                        <TableRow
                                                            className={LCSR.productRow}
                                                            sx={{
                                                                paddingBottom: '3rem',
                                                                height: 50,
                                                                backgroundColor: getRowBackgroundColor(product),
                                                            }}
                                                        >
                                                            <TableCell
                                                                className={LCSR.productImageCell}
                                                                align="right"
                                                                style={{ textAlign: 'left', margin: 0, padding: '8px' }}
                                                                width={100}
                                                            >
                                                                <div
                                                                    className={LCSR.productImageWrap}
                                                                    style={{
                                                                        position: 'relative',
                                                                        display: 'inline-block',
                                                                        padding: '2px',
                                                                        backgroundColor: '#f8f9fa',
                                                                        borderRadius: '4px',
                                                                        transition: 'all 0.3s ease',
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.backgroundColor =
                                                                            '#f0f7f0';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.backgroundColor =
                                                                            '#f8f9fa';
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={product.imageUrl}
                                                                        style={{
                                                                            width: '110px',
                                                                            height: 'auto',
                                                                            display: 'block',
                                                                            borderRadius: '4px',
                                                                        }}
                                                                        alt=""
                                                                    />
                                                                    <div
                                                                        className={LCSR.inputIconProductImageZoom}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            left: '50%',
                                                                            transform: 'translate(-50%, -50%)',
                                                                            cursor: 'pointer',
                                                                            color: '#5b99f3',
                                                                            opacity: 0,
                                                                            transition: 'all 0.3s ease',
                                                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                                            borderRadius: '50%',
                                                                            padding: '8px',
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.opacity = '1';
                                                                            e.currentTarget.style.backgroundColor =
                                                                                'rgba(255, 255, 255, 0.9)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.opacity = '0';
                                                                            e.currentTarget.style.backgroundColor =
                                                                                'rgba(255, 255, 255, 0.8)';
                                                                        }}
                                                                        onClick={() =>
                                                                            handleOpenModal(product.imageUrl)
                                                                        }
                                                                    >
                                                                        <ZoomInIcon
                                                                            sx={{ fontSize: '32px' }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell
                                                                className={LCSR.productBarcodeCell}
                                                                component="th"
                                                                scope="row"
                                                                style={{ textAlign: 'center' }}
                                                                width={100}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <p>
                                                                        <b>{product.barcode}</b>
                                                                    </p>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <div className={lcsrCls('linktxt', LCSR.linkProductDetail)}>
                                                                    <Link to={`/productdetail/${product?.optionId}`}>
                                                                        <b>
                                                                            {product.ozelKod1} - {product.renkKod}
                                                                        </b>
                                                                    </Link>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <p>{product.productDescriptionOnWebSite}</p>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <p>{product.size}</p>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <FormControl fullWidth disabled>
                                                                    <Select
                                                                        className={LCSR.inputSelectRowReturnReason}
                                                                        value={
                                                                            returnListData
                                                                                ?.shippingListForBackofficeModels[0]
                                                                                ?.shippingListStatusDecription || ''
                                                                        }
                                                                        displayEmpty
                                                                        inputProps={{
                                                                            'aria-label': 'Reason description',
                                                                        }}
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: 'rgba(3, 2, 2, 0.12)',
                                                                            },
                                                                            pointerEvents: 'none',
                                                                            minWidth: '120px',
                                                                        }}
                                                                    >
                                                                        <MenuItem
                                                                            value={
                                                                                returnListData
                                                                                    ?.shippingListForBackofficeModels[0]
                                                                                    ?.shippingListStatusDecription
                                                                            }
                                                                        >
                                                                            {t('lostCargoReturnPageBuilder.lostCargo')}
                                                                        </MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <FormControl fullWidth>
                                                                    <Select
                                                                        className={LCSR.inputSelectRowQuantity}
                                                                        value={
                                                                            selectedProducts.get(product.barcode) || 0
                                                                        }
                                                                        displayEmpty
                                                                        onChange={(e) =>
                                                                            handleQuantityChange(
                                                                                product.barcode,
                                                                                Number(e.target.value)
                                                                            )
                                                                        }
                                                                        inputProps={{ 'aria-label': 'piece' }}
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: 'rgba(3, 2, 2, 0.12)',
                                                                            },
                                                                            minWidth: '120px',
                                                                        }}
                                                                    >
                                                                        {Array.from(
                                                                            { length: product.quantity + 1 },
                                                                            (_, i) => i
                                                                        ).map((qty) => (
                                                                            <MenuItem key={qty} value={qty}>
                                                                                {qty}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{ textAlign: 'center' }}
                                                                width={1}
                                                            >
                                                                <p>{product.salePrice}</p>
                                                            </TableCell>
                                                        </TableRow>
                                                    </>
                                                )
                                            )}
                                    </TableBody>
                                </Table>
                                <Box
                                    className={LCSR.tableFooterSummary}
                                    sx={{
                                        display: 'flex',
                                        margin: '16px 8px',
                                        padding: '8px',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 'bold', fontSize: '16px', color: '#1976d2' }}>
                                        {`${t('lostCargoReturnPageBuilder.selectedProducts')}: ${getTotalSelectedQuantity()} / ${getTotalProductCount()}`}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>{`${t(
                                        'return.totalPieces'
                                    )}: ${getTotalProductCount()}`}</Typography>
                                </Box>
                            </TableContainer>
                    )}
                </Grid>
                <Grid
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                    size={{
                        xs: 12,
                    }}
                >
                    <SCUIButton
                        className={LCSR.inputButtonCreateReturnRequest}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        text={t('lostCargoReturnPageBuilder.createReturnRequest')}
                        onClick={() => handleCreateRequest()}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export default LostCargoCustomGrid;
